import { CircularProgress, Typography } from "@mui/material";
import { classes } from "portal/theme/theme";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { Objective as ObjectiveProto } from "protos/rtc/jobs";
import { reprioritize } from "./reprioritize";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Section } from "portal/components/map/navigation/Section";
import { SortableContainer } from "portal/components/Sortable";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useStandardDragDropSensors } from "portal/utils/hooks/useStandardDragDropSensors";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import React, { FC, useState } from "react";

interface ObjectiveReorderListProps {
  jobId: number;
  objectives: ObjectiveProto[];
}

export const ObjectiveReorderList: FC<ObjectiveReorderListProps> = ({
  jobId,
  objectives,
}) => {
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const selection = useSelector(jobExplorer.selectors.selectSelection);
  const focus = useSelector(jobExplorer.selectors.selectFocus);
  const [updateObjectivePriorities] = useMutationPopups(
    rtcJobsApi.endpoints.updateObjectivePriorities.useMutation()
  );
  const sensors = useStandardDragDropSensors();
  const dispatch = useDispatch();
  const focusObjective = (objective: ObjectiveProto): void => {
    dispatch(
      jobExplorer.actions.setFocus({
        type: JobSelectionType.OBJECTIVE,
        selection: objective,
      })
    );
  };

  const selectObjective = (objective: ObjectiveProto): void => {
    dispatch(
      jobExplorer.actions.setSelection({
        type: JobSelectionType.OBJECTIVE,
        selection: objective,
      })
    );
  };

  interface SortableObjective {
    id: string;
    index: number;
    objective: ObjectiveProto;
  }

  const render: (o: SortableObjective) => JSX.Element = ({
    index,
    objective,
  }) => {
    return (
      <button
        style={{
          all: "unset",
        }}
        className={classes(
          "flex flex-row items-center py-2 cursor-grab hover:bg-lighten-100 w-full",
          focus?.type === JobSelectionType.OBJECTIVE &&
            focus.selection.id === objective.id &&
            "bg-lighten-100",
          selection?.type === JobSelectionType.OBJECTIVE &&
            selection.selection.id === objective.id &&
            "bg-carbon-blue"
        )}
        onMouseOver={() => focusObjective(objective)}
        onFocus={() => focusObjective(objective)}
        onClick={() => selectObjective(objective)}
      >
        <DragIndicatorIcon
          className={classes("ml-2 mr-2", { "opacity-0": readOnly })}
        />
        <span className={classes({ "text-zinc-400": readOnly })}>
          {index + 1}. {objective.name}
        </span>
      </button>
    );
  };

  const sortableObjectives: SortableObjective[] = objectives.map(
    (objective, index) => ({
      id: `obj-${objective.id}`,
      index,
      objective,
    })
  );

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortableObjectives.findIndex(
        ({ id }) => id === active.id
      );
      const newIndex = sortableObjectives.findIndex(({ id }) => id === over.id);

      if (oldIndex === -1) {
        console.error(
          `ObjectiveReorderList: couldn't find active id ${active.id}`
        );
        return;
      }

      if (newIndex === -1) {
        console.error(`ObjectiveReorderList: couldn't find over id ${over.id}`);
        return;
      }

      const { modifiedPriorities } = reprioritize(
        objectives,
        oldIndex,
        newIndex
      );

      // turn off drag-drop while we do the potentially non-atomic update to
      // the objective priority list on the server
      setReadOnly(true);
      await updateObjectivePriorities({
        jobId,
        changes: [...modifiedPriorities.entries()].map(([id, priority]) => ({
          id,
          priority,
        })),
      });
      setReadOnly(false);
    }
  }

  return (
    <Section
      header={
        <div className="flex flex-row items-center">
          <Typography>{t("models.autotractor.objective_other")}</Typography>
          {readOnly && (
            <CircularProgress
              className="ml-4"
              size="1rem"
              variant="indeterminate"
            />
          )}
        </div>
      }
    >
      <div className="overflow-x-hidden">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContainer
            id="objectives"
            items={sortableObjectives}
            render={render}
            disabled={readOnly}
          />
        </DndContext>
      </div>
    </Section>
  );
};
